import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Calendar, Receipt, AlertCircle, ChevronRight,
  Clock, MapPin, User
} from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { formatDate, formatCurrency, formatScheduleLabel } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import { remainingHoursLabelFromTotals } from '@/lib/prepaidCards';
import { getPortalToken } from './portalStorage';

export default function PortalDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = getPortalToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await api.get('/portal/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        window.location.href = '/portal/login';
      } else {
        toast.error('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="portal-dashboard">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Willkommen!</h1>
          <p className="text-slate-600">Hier ist Ihre Übersicht</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-red/10 rounded-lg">
                  <Users className="w-5 h-5 text-brand-red" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Kinder</p>
                  <p className="text-2xl font-bold">{dashboard?.children?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Termine (7 Tage)</p>
                  <p className="text-2xl font-bold">{dashboard?.upcoming_lessons?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={dashboard?.total_open_amount > 0 ? "border-brand-red/20 bg-brand-red/5" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${dashboard?.total_open_amount > 0 ? "bg-brand-red/10" : "bg-emerald-100"}`}>
                  <Receipt className={`w-5 h-5 ${dashboard?.total_open_amount > 0 ? "text-brand-red" : "text-emerald-600"}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Offene Beträge</p>
                  <p className="text-2xl font-bold">{formatCurrency(dashboard?.total_open_amount || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Children */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Meine Kinder</CardTitle>
            <Link to="/portal/children">
              <Button variant="ghost" size="sm">
                Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.children?.length === 0 ? (
              <p className="text-slate-500 text-sm">Keine Kinder gefunden</p>
            ) : (
              <div className="space-y-3">
                {dashboard?.children?.map((child) => (
                  <div 
                    key={child.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-red/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-brand-red" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{child.first_name} {child.last_name}</p>
                        <p className="text-sm text-slate-500">
                          {child.enrollments_count} Kursbelegung(en) • {child.active_contracts_count} Vertrag/Verträge
                          {child.prepaid_remaining_units > 0 && (
                            <> • {remainingHoursLabelFromTotals(child.prepaid_remaining_units, child.prepaid_remaining_minutes)}</>
                          )}
                        </p>
                      </div>
                    </div>
                    <Badge className={child.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100'}>
                      {statusLabel(child.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Lessons */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Nächste Termine</CardTitle>
            <Link to="/portal/schedule">
              <Button variant="ghost" size="sm">
                Stundenplan <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.upcoming_lessons?.length === 0 ? (
              <p className="text-slate-500 text-sm">Keine Termine in den nächsten 7 Tagen</p>
            ) : (
              <div className="space-y-3">
                {dashboard?.upcoming_lessons?.slice(0, 5).map((lesson, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-center min-w-[50px]">
                        <p className="text-xs text-slate-500">{formatDate(lesson.date)}</p>
                        <p className="font-bold text-brand-red">{lesson.time}</p>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{formatScheduleLabel(lesson.course_name)}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          {lesson.teacher_name && <span>{lesson.teacher_name}</span>}
                          {lesson.room_name && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {lesson.room_name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline">{lesson.student_name}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Open Invoices */}
        {dashboard?.open_invoices?.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-brand-red" />
                Offene Rechnungen
              </CardTitle>
              <Link to="/portal/invoices">
                <Button variant="ghost" size="sm">
                  Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.open_invoices?.map((invoice) => (
                  <div 
                    key={invoice.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${invoice.status === 'overdue' ? 'bg-brand-red/5' : 'bg-slate-50'}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-slate-500">
                        {invoice.student_name} • Fällig: {invoice.due_date ? formatDate(invoice.due_date) : '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${invoice.status === 'overdue' ? 'text-brand-red' : 'text-slate-800'}`}>{formatCurrency(invoice.total_amount - invoice.paid_amount)}</p>
                      <Badge className={
                        invoice.status === 'overdue'
                          ? 'bg-brand-red/10 text-brand-red'
                          : 'bg-slate-100 text-slate-700'
                      }>
                        {invoice.status === 'overdue' ? 'Überfällig' : 'Offen'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
